// src/validation/customer.ts
var EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
var PHONE_REGEX = /^(\+62|62|0)[0-9]{9,13}$/;
var INSTAGRAM_REGEX = /^[a-zA-Z0-9._]{1,30}$/;
function isValidEmail(email) {
  if (!email || typeof email !== "string") return false;
  const trimmed = email.trim();
  if (trimmed.length > 254) return false;
  return EMAIL_REGEX.test(trimmed);
}
function isValidPhoneNumber(phone) {
  if (!phone || typeof phone !== "string") return false;
  const cleaned = phone.replace(/[\s-]/g, "");
  return PHONE_REGEX.test(cleaned);
}
function isValidInstagramHandle(handle) {
  if (!handle || typeof handle !== "string") return false;
  const cleaned = handle.startsWith("@") ? handle.slice(1) : handle;
  return INSTAGRAM_REGEX.test(cleaned);
}
function isValidName(name) {
  if (!name || typeof name !== "string") return false;
  return name.trim().length > 0;
}
function validateCustomerInfo(customer) {
  const errors = [];
  if (!customer.name || !isValidName(customer.name)) {
    errors.push({
      field: "name",
      message: "Name is required"
    });
  }
  if (!customer.phone) {
    errors.push({
      field: "phone",
      message: "Phone number is required"
    });
  } else if (!isValidPhoneNumber(customer.phone)) {
    errors.push({
      field: "phone",
      message: "Invalid phone number format"
    });
  }
  if (!customer.email) {
    errors.push({
      field: "email",
      message: "Email is required"
    });
  } else if (!isValidEmail(customer.email)) {
    errors.push({
      field: "email",
      message: "Invalid email format"
    });
  }
  if (customer.instagram && !isValidInstagramHandle(customer.instagram)) {
    errors.push({
      field: "instagram",
      message: "Invalid Instagram handle format"
    });
  }
  return {
    isValid: errors.length === 0,
    errors
  };
}

// src/validation/product.ts
function validateProduct(product) {
  const errors = [];
  if (!product.name || product.name.trim().length === 0) {
    errors.push({ field: "name", message: "Product name is required" });
  }
  if (!product.categoryId) {
    errors.push({ field: "categoryId", message: "Category is required" });
  }
  if (product.shotCount === void 0 || product.shotCount <= 0) {
    errors.push({ field: "shotCount", message: "Shot count must be a positive number" });
  } else if (![8, 16].includes(product.shotCount)) {
    errors.push({ field: "shotCount", message: "Shot count must be 8 or 16" });
  }
  if (product.printCount === void 0 || product.printCount <= 0) {
    errors.push({ field: "printCount", message: "Print count must be a positive number" });
  } else if (![1, 2, 4].includes(product.printCount)) {
    errors.push({ field: "printCount", message: "Print count must be 1, 2, or 4" });
  }
  if (!product.paperType) {
    errors.push({ field: "paperType", message: "Paper type is required" });
  } else if (!["collage_4x6", "strip"].includes(product.paperType)) {
    errors.push({ field: "paperType", message: "Paper type must be collage_4x6 or strip" });
  }
  if (product.basePrice === void 0 || product.basePrice < 0) {
    errors.push({ field: "basePrice", message: "Base price must be a non-negative number" });
  }
  return {
    isValid: errors.length === 0,
    errors
  };
}
function validateCategory(category) {
  const errors = [];
  if (!category.name || category.name.trim().length === 0) {
    errors.push({ field: "name", message: "Category name is required" });
  }
  return {
    isValid: errors.length === 0,
    errors
  };
}
function validateOutletPricing(pricing) {
  const errors = [];
  if (pricing.originalPrice === void 0 || pricing.originalPrice < 0) {
    errors.push({ field: "originalPrice", message: "Original price must be a non-negative number" });
  }
  if (pricing.discountedPrice !== void 0) {
    if (pricing.discountedPrice < 0) {
      errors.push({ field: "discountedPrice", message: "Discounted price must be a non-negative number" });
    }
    if (pricing.originalPrice !== void 0 && pricing.discountedPrice > pricing.originalPrice) {
      errors.push({ field: "discountedPrice", message: "Discounted price cannot exceed original price" });
    }
  }
  return {
    isValid: errors.length === 0,
    errors
  };
}
function getPrintQuantityForProduct(product) {
  if (product.paperType === "collage_4x6") {
    return product.shotCount === 8 ? 1 : 2;
  }
  return product.shotCount === 8 ? 2 : 4;
}

// src/validation/frame.ts
var SUPPORTED_FRAME_FORMATS = ["image/png", "image/jpeg", "image/webp"];
var FRAME_DIMENSIONS = {
  collage_4x6: {
    minWidth: 1200,
    minHeight: 1748,
    maxWidth: 4800,
    maxHeight: 7200,
    aspectRatio: 2 / 3
    // 4:6 ratio
  },
  strip: {
    minWidth: 600,
    minHeight: 1800,
    maxWidth: 2400,
    maxHeight: 7200,
    aspectRatio: 1 / 3
    // strip ratio
  }
};
function isValidFrameFormat(mimeType) {
  return SUPPORTED_FRAME_FORMATS.includes(mimeType);
}
function isValidFrameDimensions(width, height, paperType) {
  const constraints = FRAME_DIMENSIONS[paperType];
  if (!constraints) return false;
  return width >= constraints.minWidth && width <= constraints.maxWidth && height >= constraints.minHeight && height <= constraints.maxHeight;
}
function validateFrame(frame) {
  const errors = [];
  if (!frame.name || frame.name.trim().length === 0) {
    errors.push({ field: "name", message: "Frame name is required" });
  }
  if (!frame.frameCategoryId) {
    errors.push({ field: "frameCategoryId", message: "Frame category is required" });
  }
  if (!frame.paperType) {
    errors.push({ field: "paperType", message: "Paper type is required" });
  } else if (!["collage_4x6", "strip"].includes(frame.paperType)) {
    errors.push({ field: "paperType", message: "Paper type must be collage_4x6 or strip" });
  }
  return {
    isValid: errors.length === 0,
    errors
  };
}
function validateFrameUpload(frame, imageMetadata) {
  const baseValidation = validateFrame(frame);
  const errors = [...baseValidation.errors];
  if (!isValidFrameFormat(imageMetadata.mimeType)) {
    errors.push({
      field: "image",
      message: `Unsupported image format. Supported formats: ${SUPPORTED_FRAME_FORMATS.join(", ")}`
    });
  }
  if (frame.paperType && !isValidFrameDimensions(imageMetadata.width, imageMetadata.height, frame.paperType)) {
    const constraints = FRAME_DIMENSIONS[frame.paperType];
    errors.push({
      field: "image",
      message: `Invalid dimensions. Required: ${constraints.minWidth}x${constraints.minHeight} to ${constraints.maxWidth}x${constraints.maxHeight}`
    });
  }
  return {
    isValid: errors.length === 0,
    errors
  };
}

// src/formatting/currency.ts
function formatRupiah(amount) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}
function formatRupiahValue(amount) {
  return new Intl.NumberFormat("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}
function parseRupiah(formatted) {
  const cleaned = formatted.replace(/[Rp\s.]/g, "").replace(",", ".");
  return parseFloat(cleaned) || 0;
}
function calculateDiscountPercentage(originalPrice, discountedPrice) {
  if (originalPrice <= 0) return 0;
  const discount = (originalPrice - discountedPrice) / originalPrice * 100;
  return Math.round(discount);
}

// src/formatting/date.ts
function formatDate(date) {
  const d = new Date(date);
  return new Intl.DateTimeFormat("id-ID", {
    year: "numeric",
    month: "long",
    day: "numeric"
  }).format(d);
}
function formatDateTime(date) {
  const d = new Date(date);
  return new Intl.DateTimeFormat("id-ID", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(d);
}
function formatDateShort(date) {
  const d = new Date(date);
  return new Intl.DateTimeFormat("id-ID", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(d);
}
function formatRelativeTime(date) {
  const d = new Date(date);
  const now = /* @__PURE__ */ new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.floor(diffMs / 1e3);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  if (diffSec < 60) return "Baru saja";
  if (diffMin < 60) return `${diffMin} menit yang lalu`;
  if (diffHour < 24) return `${diffHour} jam yang lalu`;
  if (diffDay < 7) return `${diffDay} hari yang lalu`;
  return formatDate(d);
}
function formatTime(date) {
  const d = new Date(date);
  return new Intl.DateTimeFormat("id-ID", {
    hour: "2-digit",
    minute: "2-digit"
  }).format(d);
}

// src/formatting/transaction.ts
function randomHex(length) {
  const chars = "0123456789ABCDEF";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}
function generateTransactionId() {
  const date = /* @__PURE__ */ new Date();
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, "");
  const randomPart = randomHex(8);
  return `PB-${dateStr}-${randomPart}`;
}
function isValidTransactionId(transactionId) {
  if (!transactionId || typeof transactionId !== "string") return false;
  const regex = /^PB-\d{8}-[A-Z0-9]{8}$/;
  return regex.test(transactionId);
}
function getDateFromTransactionId(transactionId) {
  if (!isValidTransactionId(transactionId)) return null;
  const dateStr = transactionId.slice(3, 11);
  const year = parseInt(dateStr.slice(0, 4), 10);
  const month = parseInt(dateStr.slice(4, 6), 10) - 1;
  const day = parseInt(dateStr.slice(6, 8), 10);
  return new Date(year, month, day);
}
function formatTransactionIdForDisplay(transactionId) {
  return transactionId;
}
function generateTransactionQRData(transactionId) {
  return JSON.stringify({
    type: "photobooth_transaction",
    version: 1,
    transactionId,
    timestamp: Date.now()
  });
}
function parseTransactionQRData(qrData) {
  try {
    const parsed = JSON.parse(qrData);
    if (parsed.type === "photobooth_transaction" && parsed.transactionId) {
      return parsed.transactionId;
    }
    return null;
  } catch {
    if (isValidTransactionId(qrData)) {
      return qrData;
    }
    return null;
  }
}
export {
  FRAME_DIMENSIONS,
  SUPPORTED_FRAME_FORMATS,
  calculateDiscountPercentage,
  formatDate,
  formatDateShort,
  formatDateTime,
  formatRelativeTime,
  formatRupiah,
  formatRupiahValue,
  formatTime,
  formatTransactionIdForDisplay,
  generateTransactionId,
  generateTransactionQRData,
  getDateFromTransactionId,
  getPrintQuantityForProduct,
  isValidEmail,
  isValidFrameDimensions,
  isValidFrameFormat,
  isValidInstagramHandle,
  isValidName,
  isValidPhoneNumber,
  isValidTransactionId,
  parseRupiah,
  parseTransactionQRData,
  validateCategory,
  validateCustomerInfo,
  validateFrame,
  validateFrameUpload,
  validateOutletPricing,
  validateProduct
};
